// =============================================================================
// FireISP 5.0 — NAS Management
// =============================================================================
// Standalone page at /nas. Lists RADIUS NAS / network access servers with a
// status filter, paginated table, and "New NAS" create modal plus per-row Edit
// and Delete (soft-delete). All mutations go through the typed `api` client +
// React Query, invalidating the ['nas'] query so the list refreshes
// automatically.
// =============================================================================

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import { styles, modalStyles, RequiredMark, capitalize } from './crudStyles';
import { Pagination } from '@/components/Pagination';
import { LoadingState } from '@/components/FetchStates';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Nas {
  id: number;
  name: string;
  ip_address: string;
  ipv6_address: string | null;
  type: string | null;
  ports: number | null;
  coa_port: number | null;
  location: string | null;
  site_id?: number | null;
  secondary_nas_id: number | null;
  health_status: string;
  last_health_check_at: string | null;
  description: string | null;
  status: string;
  api_port?: number | null;
  api_username?: string | null;
  api_use_tls?: boolean | null;
  access_mode?: 'direct' | 'nated';
  /** Keep the NAS active while excluding it from automated RouterOS PPPoE diagnostics polling/readiness. */
  maintenance_mode?: boolean | number;
}

interface NasResponse {
  data: Nas[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface NasBody {
  name: string;
  ip_address?: string;
  ipv6_address?: string;
  secret?: string;
  type?: string;
  ports?: number;
  coa_port?: number;
  location?: string;
  secondary_nas_id?: number;
  description?: string;
  status?: string;
  api_port?: number;
  api_username?: string;
  api_password?: string;
  api_use_tls?: boolean;
  access_mode?: 'direct' | 'nated';
  maintenance_mode?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUSES = ['active', 'inactive'];
const STATUS_FILTER_OPTIONS = ['', ...STATUSES];

// ---------------------------------------------------------------------------
// Fetch / mutate helpers
// ---------------------------------------------------------------------------

async function fetchNas(page: number, pageSize: number, statusFilter: string): Promise<NasResponse> {
  const query: Record<string, string | number> = { page, limit: pageSize };
  if (statusFilter) query.status = statusFilter;
  const res = await api.GET('/nas', { params: { query: query as never } });
  if (res.error) throw new Error('Failed to load NAS devices');
  return res.data as unknown as NasResponse;
}

async function createNas(body: NasBody): Promise<Nas | null> {
  const res = await api.POST('/nas', { body: body as never });
  if (res.error) throw new Error('Failed to create NAS');
  const d = res.data as { data?: Nas } | null;
  return d?.data ?? null;
}

async function updateNas(id: number, body: Partial<NasBody>): Promise<void> {
  const res = await api.PUT('/nas/{id}', { params: { path: { id } }, body: body as never });
  if (res.error) throw new Error('Failed to update NAS');
}

export async function deleteNas(id: number): Promise<void> {
  const res = await api.DELETE('/nas/{id}', { params: { path: { id } } });
  if (res.error) throw new Error('Failed to delete NAS');
}

interface SeedBody {
  radiusAddress: string;
  authPort?: number;
  acctPort?: number;
  coaPort?: number;
  interimUpdate?: string;
  seedQueueTree?: boolean;
  queueParent?: string;
  totalDownloadMbps?: number;
  totalUploadMbps?: number;
  seedQueueTypes?: boolean;
  seedPriorityQueues?: boolean;
  seedPppoeServer?: boolean;
  pppoeInterface?: string;
  pppoeServiceName?: string;
  pppoeProfileName?: string;
  pppoeLocalAddress?: string;
  pppoeParentQueue?: string;
  seedWalledGarden?: boolean;
  suspendedListName?: string;
  portalAddress?: string;
  redirectPorts?: string;
  redirectToPort?: number;
  redirectEnabled?: boolean;
  seedRealtimePriority?: boolean;
  sipRtpPorts?: string;
  voipNetworks?: string;
  trustClientDscp?: boolean;
  realtimeParent?: string;
  realtimeMaxMbps?: number;
}

interface SeedStep {
  step: string;
  status: string;
  detail: string;
}

interface SeedResult {
  ok: boolean;
  host: string;
  port: number;
  tls: boolean;
  steps: SeedStep[];
}

export async function seedNasDevice(id: number, body: SeedBody): Promise<SeedResult> {
  const res = (await api.POST('/nas/{id}/seed', {
    params: { path: { id } },
    body: body as never,
  })) as {
    data?: { data?: SeedResult };
    error?: { error?: { message?: string; details?: Array<{ field?: string; message?: string }> } };
  };
  if (res.error) {
    const err = res.error?.error;
    // Surface field-level validation feedback (422) instead of a bare
    // "Validation failed" so the admin knows which input the server rejected.
    const fieldMsgs = (err?.details ?? []).map((d) => d.message ?? d.field).filter(Boolean);
    const message = fieldMsgs.length
      ? `${err?.message ?? 'Validation failed'}: ${fieldMsgs.join('; ')}`
      : (err?.message ?? 'Router unreachable');
    throw new Error(message);
  }
  return res.data?.data as SeedResult;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string }> = {
    active: { bg: '#d1fae5', color: '#065f46' },
    inactive: { bg: '#fef3c7', color: '#92400e' },
  };
  const s = map[status] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: '0.72rem',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Health badge
// ---------------------------------------------------------------------------

function HealthBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string }> = {
    up: { bg: '#d1fae5', color: '#065f46' },
    down: { bg: '#fee2e2', color: '#991b1b' },
    unknown: { bg: '#f3f4f6', color: '#374151' },
  };
  const s = map[status] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: '0.72rem',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// NAS form modal (create + edit)
// ---------------------------------------------------------------------------

interface NasModalProps {
  nas: Nas | null;
  onClose: () => void;
  onSaved: () => void;
  /** Called after a successful *create* (not edit) with the newly created NAS. */
  onCreated?: (nas: Nas) => void;
}

export function NasModal({ nas, onClose, onSaved, onCreated }: NasModalProps) {
  const isEdit = nas !== null;
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: nas?.name ?? '',
    access_mode: (nas?.access_mode ?? 'direct') as 'direct' | 'nated',
    ip_address: nas?.ip_address ?? '',
    ipv6_address: nas?.ipv6_address ?? '',
    secret: '',
    type: nas?.type ?? '',
    ports: nas?.ports != null ? String(nas.ports) : '',
    coa_port: nas?.coa_port != null ? String(nas.coa_port) : '3799',
    location: nas?.location ?? '',
    secondary_nas_id: nas?.secondary_nas_id != null ? String(nas.secondary_nas_id) : '',
    description: nas?.description ?? '',
    status: nas?.status ?? 'active',
    api_port: nas?.api_port != null ? String(nas.api_port) : '8728',
    api_username: nas?.api_username ?? '',
    api_password: '',
    // The API serves api_use_tls as a MySQL tinyint (0/1). `?? false` only
    // replaces null/undefined, so an existing 0/1 would survive as a *number*
    // and the server's strict-boolean validator rejects the edit with a 422
    // ("api_use_tls must be a boolean"). Coerce to a real boolean on load.
    api_use_tls: Boolean(nas?.api_use_tls),
    // MySQL may serialize this boolean as 0/1, just like api_use_tls.
    maintenance_mode: Boolean(nas?.maintenance_mode),
  });
  const isNated = form.access_mode === 'nated';
  const [error, setError] = useState('');

  function setField(name: string, value: unknown) {
    setForm(prev => ({ ...prev, [name]: value }));
  }

  const mutation = useMutation({
    mutationFn: async (): Promise<Nas | null> => {
      const body: NasBody = {
        name: form.name.trim(),
        access_mode: form.access_mode,
        status: form.status,
        maintenance_mode: Boolean(form.maintenance_mode),
      };
      // ip_address is only sent for direct mode; for nated the server allocates
      // the WireGuard tunnel address and uses it as ip_address.
      if (!isNated && form.ip_address.trim()) body.ip_address = form.ip_address.trim();
      if (form.ipv6_address) body.ipv6_address = form.ipv6_address.trim();
      if (form.secret) body.secret = form.secret;
      if (form.type) body.type = form.type.trim();
      if (form.ports) body.ports = Number(form.ports);
      if (form.coa_port) body.coa_port = Number(form.coa_port);
      if (form.location) body.location = form.location.trim();
      if (form.secondary_nas_id) body.secondary_nas_id = Number(form.secondary_nas_id);
      if (form.description) body.description = form.description;
      if (form.api_port) body.api_port = Number(form.api_port);
      if (form.api_username) body.api_username = form.api_username.trim();
      if (form.api_password) body.api_password = form.api_password;
      body.api_use_tls = Boolean(form.api_use_tls);
      if (isEdit) { await updateNas(nas.id, body); return null; }
      return createNas(body);
    },
    onSuccess: (result) => {
      onSaved();
      onClose();
      if (result) onCreated?.(result);
    },
    onError: () => setError('Failed to save NAS. Check all fields and try again.'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    // ip_address only required for direct mode
    if (!isNated && !form.ip_address.trim()) {
      setError('IP address is required for direct-mode NAS.');
      return;
    }
    if (!isEdit && !form.secret) {
      setError('RADIUS shared secret is required.');
      return;
    }
    setError('');
    mutation.mutate();
  }

  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div
        style={modalStyles.panel}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? `Edit NAS ${nas.name}` : 'New NAS'}
      >
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{isEdit ? `Edit NAS #${nas.id}` : 'New NAS'}</h2>
          <button style={modalStyles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <label style={modalStyles.label}>
            Name <RequiredMark />
            <input
              style={modalStyles.input}
              type="text"
              maxLength={255}
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              required
            />
          </label>

          <label style={modalStyles.label}>
            {t('nasList.accessMode.label')}
            <select
              style={modalStyles.select}
              value={form.access_mode}
              onChange={e => setField('access_mode', e.target.value as 'direct' | 'nated')}
              aria-label={t('nasList.accessMode.label')}
              disabled={isEdit}
              title={isEdit ? t('nasList.accessMode.immutable') : undefined}
            >
              <option value="direct">{t('nasList.accessMode.direct')}</option>
              <option value="nated">{t('nasList.accessMode.nated')}</option>
            </select>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
              {t('nasList.accessMode.hint')}
            </span>
          </label>

          {isNated && (
            <p style={{
              margin: '0 0 8px',
              padding: '8px 12px',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 6,
              fontSize: '0.82rem',
              color: '#1e40af',
              lineHeight: 1.5,
            }}>
              {t('nasList.natedHint')}
            </p>
          )}

          {!isNated && (
            <label style={modalStyles.label}>
              {t('nasList.ipAddressLabel')} <RequiredMark />
              <input
                style={modalStyles.input}
                type="text"
                maxLength={45}
                value={form.ip_address}
                onChange={e => setField('ip_address', e.target.value)}
                placeholder={t('nasList.ipAddressPlaceholder')}
                required={!isNated}
              />
            </label>
          )}

          <label style={modalStyles.label}>
            IPv6 Address
            <input
              style={modalStyles.input}
              type="text"
              maxLength={45}
              value={form.ipv6_address}
              onChange={e => setField('ipv6_address', e.target.value)}
            />
          </label>

          <label style={modalStyles.label}>
            RADIUS Shared Secret {!isEdit && <RequiredMark />}
            <input
              style={modalStyles.input}
              type="password"
              maxLength={255}
              value={form.secret}
              onChange={e => setField('secret', e.target.value)}
              placeholder={isEdit ? 'Leave blank to keep current secret' : ''}
              autoComplete="new-password"
            />
          </label>

          <label style={modalStyles.label}>
            Type
            <input
              style={modalStyles.input}
              type="text"
              maxLength={50}
              value={form.type}
              onChange={e => setField('type', e.target.value)}
              placeholder="e.g. mikrotik, cisco, ubiquiti"
            />
          </label>

          <label style={modalStyles.label}>
            Ports
            <input
              style={modalStyles.input}
              type="number"
              min={0}
              value={form.ports}
              onChange={e => setField('ports', e.target.value)}
            />
          </label>

          <label style={modalStyles.label}>
            CoA Port
            <input
              style={modalStyles.input}
              type="number"
              min={1}
              max={65535}
              value={form.coa_port}
              onChange={e => setField('coa_port', e.target.value)}
              aria-label="CoA Port"
            />
          </label>

          <label style={modalStyles.label}>
            Location
            <input
              style={modalStyles.input}
              type="text"
              maxLength={200}
              value={form.location}
              onChange={e => setField('location', e.target.value)}
              aria-label="Location"
            />
          </label>

          <label style={modalStyles.label}>
            Failover NAS ID
            <input
              style={modalStyles.input}
              type="number"
              min={1}
              value={form.secondary_nas_id}
              onChange={e => setField('secondary_nas_id', e.target.value)}
              aria-label="Failover NAS ID"
            />
          </label>

          <label style={modalStyles.label}>
            Description
            <textarea
              style={{ ...modalStyles.input, minHeight: 60, resize: 'vertical' }}
              maxLength={5000}
              value={form.description}
              onChange={e => setField('description', e.target.value)}
            />
          </label>

          <label style={modalStyles.label}>
            Status
            <select
              style={modalStyles.select}
              value={form.status}
              onChange={e => setField('status', e.target.value)}
            >
              {STATUSES.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
            </select>
          </label>

          <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.maintenance_mode}
              onChange={e => setField('maintenance_mode', e.target.checked)}
              aria-describedby="nas-maintenance-mode-hint"
            />
            <span>
              <span style={{ display: 'block' }}>{t('nasList.maintenanceMode.label')}</span>
              <span
                id="nas-maintenance-mode-hint"
                style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.4 }}
              >
                {t('nasList.maintenanceMode.hint')}
              </span>
            </span>
          </label>

          <label style={modalStyles.label}>
            RouterOS API Port
            <input
              style={modalStyles.input}
              type="number"
              min={1}
              max={65535}
              value={form.api_port}
              onChange={e => setField('api_port', e.target.value)}
              placeholder="8728"
              aria-label="RouterOS API Port"
            />
          </label>

          <label style={modalStyles.label}>
            RouterOS API Username
            <input
              style={modalStyles.input}
              type="text"
              maxLength={128}
              value={form.api_username}
              onChange={e => setField('api_username', e.target.value)}
              autoComplete="off"
            />
          </label>

          <label style={modalStyles.label}>
            RouterOS API Password
            <input
              style={modalStyles.input}
              type="password"
              maxLength={255}
              value={form.api_password}
              onChange={e => setField('api_password', e.target.value)}
              placeholder="Leave blank to keep current"
              autoComplete="new-password"
            />
          </label>

          <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.api_use_tls}
              onChange={e => {
                const on = e.target.checked;
                setField('api_use_tls', on);
                // Nudge the port to the api-ssl default (8729) when enabling TLS
                // if it's still on the plain-API default (8728).
                if (on && form.api_port === '8728') setField('api_port', '8729');
              }}
              aria-label="Use TLS for RouterOS API"
            />
            Use TLS for RouterOS API
          </label>

          {error && <p style={modalStyles.error}>{error}</p>}

          <div style={modalStyles.actions}>
            <button type="button" onClick={onClose} style={styles.btnSecondary} disabled={mutation.isPending}>
              Cancel
            </button>
            <button type="submit" style={styles.btnPrimary} disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create NAS'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm dialog
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({ message, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <div style={modalStyles.backdrop} onClick={onCancel}>
      <div
        style={{ ...modalStyles.panel, maxWidth: 380 }}
        onClick={e => e.stopPropagation()}
        role="alertdialog"
        aria-label="Confirm action"
      >
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.95rem', color: 'var(--text-primary)' }}>{message}</p>
        <div style={modalStyles.actions}>
          <button onClick={onCancel} style={styles.btnSecondary}>No, go back</button>
          <button onClick={onConfirm} style={styles.btnDanger}>Yes, confirm</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seed modal — one-click RouterOS bootstrap (RADIUS + PPP AAA + CoA, optional
// queue tree + walled garden). Idempotent on the device, so safe to re-run.
// ---------------------------------------------------------------------------

const SEED_STEP_COLORS: Record<string, { bg: string; color: string }> = {
  created: { bg: '#d1fae5', color: '#065f46' },
  updated: { bg: '#dbeafe', color: '#1e40af' },
  unchanged: { bg: '#f3f4f6', color: '#374151' },
  skipped: { bg: '#f3f4f6', color: '#374151' },
  error: { bg: '#fee2e2', color: '#991b1b' },
};

interface SeedModalProps {
  nas: Nas;
  onClose: () => void;
  /**
   * Pre-fills the RADIUS server address. Every NAS reaches FireISP over the
   * WireGuard tunnel, so this should be the hub tunnel IP (e.g. 10.255.0.1) —
   * that way RADIUS rides the tunnel and no management port faces the internet.
   */
  defaultRadiusAddress?: string;
}

/**
 * True if `v` is an IPv4 or IPv6 literal. RouterOS's /radius `address` accepts
 * only an IP (a hostname fails on the device), so the Seed form validates the
 * RADIUS address up front. The backend re-checks authoritatively via net.isIP.
 */
function isIpAddress(v: string): boolean {
  const s = v.trim();
  // IPv4 dotted-quad with 0–255 octets.
  if (/^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/.test(s)) return true;
  // IPv6 (loose): hex groups separated by colons, incl. "::" compression.
  if (s.includes(':') && /^[0-9a-fA-F:]+$/.test(s)) return true;
  return false;
}

export function SeedModal({ nas, onClose, defaultRadiusAddress }: SeedModalProps) {
  const [form, setForm] = useState({
    // Prefill the FireISP hub's WireGuard tunnel IP (an IP literal, which RouterOS's
    // /radius `address` requires — a hostname trips a cryptic device error). Routing
    // RADIUS over the tunnel means only 80/443 + the WG port need be public on the
    // server. Falls back to blank when the hub IP isn't known yet.
    radiusAddress: defaultRadiusAddress ?? '',
    authPort: '1812',
    acctPort: '1813',
    coaPort: nas.coa_port != null ? String(nas.coa_port) : '3799',
    interimUpdate: '5m',
    seedQueueTree: false,
    queueParent: 'global',
    totalDownloadMbps: '',
    totalUploadMbps: '',
    seedQueueTypes: false,
    seedPriorityQueues: false,
    seedPppoeServer: false,
    pppoeInterface: '',
    pppoeServiceName: 'FireISP-Internet',
    pppoeProfileName: 'fireisp-pppoe',
    pppoeLocalAddress: '',
    pppoeParentQueue: '',
    seedWalledGarden: false,
    suspendedListName: 'fireisp-suspended',
    portalAddress: '',
    redirectPorts: '80,443',
    redirectToPort: '80',
    redirectEnabled: true,
    seedRealtimePriority: false,
    sipRtpPorts: '5060,5061,10000-20000',
    voipNetworks: '',
    trustClientDscp: false,
    realtimeParent: 'global',
    realtimeMaxMbps: '',
  });
  const [error, setError] = useState('');
  const [result, setResult] = useState<SeedResult | null>(null);

  function setField(name: string, value: unknown) {
    setForm(prev => ({ ...prev, [name]: value }));
  }

  const mutation = useMutation({
    mutationFn: () => {
      const body: SeedBody = { radiusAddress: form.radiusAddress.trim() };
      if (form.authPort) body.authPort = Number(form.authPort);
      if (form.acctPort) body.acctPort = Number(form.acctPort);
      if (form.coaPort) body.coaPort = Number(form.coaPort);
      if (form.interimUpdate) body.interimUpdate = form.interimUpdate.trim();
      body.seedQueueTree = form.seedQueueTree;
      if (form.seedQueueTree && form.queueParent) body.queueParent = form.queueParent.trim();
      body.seedQueueTypes = form.seedQueueTypes;
      body.seedPriorityQueues = form.seedPriorityQueues;
      // Total down/up feed both the queue-tree skeleton and the §4 POP-limit classes.
      if (form.seedQueueTree || form.seedPriorityQueues) {
        if (form.totalDownloadMbps) body.totalDownloadMbps = Number(form.totalDownloadMbps);
        if (form.totalUploadMbps) body.totalUploadMbps = Number(form.totalUploadMbps);
      }
      body.seedPppoeServer = form.seedPppoeServer;
      if (form.seedPppoeServer) {
        if (form.pppoeInterface) body.pppoeInterface = form.pppoeInterface.trim();
        if (form.pppoeServiceName) body.pppoeServiceName = form.pppoeServiceName.trim();
        if (form.pppoeProfileName) body.pppoeProfileName = form.pppoeProfileName.trim();
        if (form.pppoeLocalAddress) body.pppoeLocalAddress = form.pppoeLocalAddress.trim();
        if (form.pppoeParentQueue) body.pppoeParentQueue = form.pppoeParentQueue.trim();
      }
      body.seedWalledGarden = form.seedWalledGarden;
      if (form.seedWalledGarden) {
        if (form.suspendedListName) body.suspendedListName = form.suspendedListName.trim();
        if (form.portalAddress) body.portalAddress = form.portalAddress.trim();
        if (form.redirectPorts) body.redirectPorts = form.redirectPorts.trim();
        if (form.redirectToPort) body.redirectToPort = Number(form.redirectToPort);
        body.redirectEnabled = form.redirectEnabled;
      }
      body.seedRealtimePriority = form.seedRealtimePriority;
      if (form.seedRealtimePriority) {
        if (form.sipRtpPorts) body.sipRtpPorts = form.sipRtpPorts.trim();
        if (form.voipNetworks) body.voipNetworks = form.voipNetworks.trim();
        body.trustClientDscp = form.trustClientDscp;
        if (form.realtimeParent) body.realtimeParent = form.realtimeParent.trim();
        if (form.realtimeMaxMbps) body.realtimeMaxMbps = Number(form.realtimeMaxMbps);
      }
      return seedNasDevice(nas.id, body);
    },
    onSuccess: (res) => {
      setResult(res);
      setError('');
    },
    onError: (e: unknown) => {
      setResult(null);
      setError(e instanceof Error ? e.message : 'Seeding failed. Check the API connection and try again.');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.radiusAddress.trim()) {
      setError('FireISP RADIUS address is required.');
      return;
    }
    if (!isIpAddress(form.radiusAddress)) {
      setError('FireISP RADIUS address must be an IP address, not a hostname — RouterOS only accepts an IP here.');
      return;
    }
    setError('');
    mutation.mutate();
  }

  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div
        style={modalStyles.panel}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Seed NAS ${nas.name}`}
      >
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Seed NAS #{nas.id} — {nas.name}</h2>
          <button style={modalStyles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {result ? (
          <div style={modalStyles.form}>
            <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: result.ok ? '#065f46' : '#991b1b' }}>
              {result.ok ? '✓ Seed completed' : '⚠ Seed completed with errors'} — {result.host}:{result.port}{result.tls ? ' (TLS)' : ''}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {result.steps.map((s, i) => {
                const c = SEED_STEP_COLORS[s.status] ?? SEED_STEP_COLORS.skipped;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.82rem' }}>
                    <span
                      style={{
                        background: c.bg, color: c.color, padding: '2px 8px', borderRadius: 12,
                        fontWeight: 600, fontSize: '0.7rem', textTransform: 'capitalize', whiteSpace: 'nowrap',
                        minWidth: 64, textAlign: 'center',
                      }}
                    >
                      {s.status}
                    </span>
                    <span style={{ color: 'var(--text-primary)' }}>
                      <strong>{s.step}</strong> — {s.detail}
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={modalStyles.actions}>
              <button type="button" onClick={() => setResult(null)} style={styles.btnSecondary}>
                Run again
              </button>
              <button type="button" onClick={onClose} style={styles.btnPrimary}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={modalStyles.form}>
            <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
              Pushes the FireISP RADIUS client, PPP AAA and CoA listener to this MikroTik over its
              RouterOS API, plus optional fq-codel queue types, priority queues, a PPPoE server, a
              walled garden and real-time/VoIP prioritisation. Idempotent — safe to re-run. The NAS
              shared secret is used automatically.
            </p>

            <label style={modalStyles.label}>
              FireISP RADIUS Address <RequiredMark />
              <input
                style={modalStyles.input}
                type="text"
                maxLength={255}
                value={form.radiusAddress}
                onChange={e => setField('radiusAddress', e.target.value)}
                placeholder="e.g. 203.0.113.10"
                required
              />
              <span style={{ fontWeight: 400, fontSize: 12, color: '#6b7280' }}>
                The IP the router uses to reach FireISP's RADIUS. Must be an IP address —
                RouterOS's /radius does not accept a hostname.
              </span>
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...modalStyles.label, flex: 1 }}>
                Auth Port
                <input style={modalStyles.input} type="number" min={1} max={65535}
                  value={form.authPort} onChange={e => setField('authPort', e.target.value)} aria-label="Auth Port" />
              </label>
              <label style={{ ...modalStyles.label, flex: 1 }}>
                Acct Port
                <input style={modalStyles.input} type="number" min={1} max={65535}
                  value={form.acctPort} onChange={e => setField('acctPort', e.target.value)} aria-label="Accounting Port" />
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...modalStyles.label, flex: 1 }}>
                CoA Port
                <input style={modalStyles.input} type="number" min={1} max={65535}
                  value={form.coaPort} onChange={e => setField('coaPort', e.target.value)} aria-label="CoA Port" />
              </label>
              <label style={{ ...modalStyles.label, flex: 1 }}>
                Interim-Update
                <input style={modalStyles.input} type="text" maxLength={16}
                  value={form.interimUpdate} onChange={e => setField('interimUpdate', e.target.value)}
                  placeholder="5m" aria-label="Interim Update" />
              </label>
            </div>

            <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.seedQueueTypes}
                onChange={e => setField('seedQueueTypes', e.target.checked)} aria-label="Seed fq-codel queue types" />
              Enable fq-codel queue types (bufferbloat prevention)
            </label>

            <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.seedQueueTree}
                onChange={e => setField('seedQueueTree', e.target.checked)} aria-label="Seed queue tree" />
              Seed global queue-tree skeleton
            </label>
            {form.seedQueueTree && (
              <label style={{ ...modalStyles.label }}>
                Queue-tree Parent
                <input style={modalStyles.input} type="text" maxLength={64}
                  value={form.queueParent} onChange={e => setField('queueParent', e.target.value)}
                  placeholder="global" aria-label="Queue parent" />
              </label>
            )}

            <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.seedPriorityQueues}
                onChange={e => setField('seedPriorityQueues', e.target.checked)} aria-label="Seed priority queues" />
              Seed Business / Residential priority queues
            </label>

            {(form.seedQueueTree || form.seedPriorityQueues) && (
              <div style={{ display: 'flex', gap: 10 }}>
                <label style={{ ...modalStyles.label, flex: 1 }}>
                  POP Total Down (Mbps)
                  <input style={modalStyles.input} type="number" min={0}
                    value={form.totalDownloadMbps} onChange={e => setField('totalDownloadMbps', e.target.value)}
                    aria-label="Total download Mbps" />
                </label>
                <label style={{ ...modalStyles.label, flex: 1 }}>
                  POP Total Up (Mbps)
                  <input style={modalStyles.input} type="number" min={0}
                    value={form.totalUploadMbps} onChange={e => setField('totalUploadMbps', e.target.value)}
                    aria-label="Total upload Mbps" />
                </label>
              </div>
            )}

            <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.seedPppoeServer}
                onChange={e => setField('seedPppoeServer', e.target.checked)} aria-label="Seed PPPoE server" />
              Seed PPPoE server + base profile
            </label>
            {form.seedPppoeServer && (
              <>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Interface <RequiredMark />
                    <input style={modalStyles.input} type="text" maxLength={64}
                      value={form.pppoeInterface} onChange={e => setField('pppoeInterface', e.target.value)}
                      placeholder="ether2" aria-label="PPPoE interface" />
                  </label>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Service Name
                    <input style={modalStyles.input} type="text" maxLength={64}
                      value={form.pppoeServiceName} onChange={e => setField('pppoeServiceName', e.target.value)}
                      placeholder="FireISP-Internet" aria-label="PPPoE service name" />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Profile Name
                    <input style={modalStyles.input} type="text" maxLength={64}
                      value={form.pppoeProfileName} onChange={e => setField('pppoeProfileName', e.target.value)}
                      placeholder="fireisp-pppoe" aria-label="PPPoE profile name" />
                  </label>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Local Address
                    <input style={modalStyles.input} type="text" maxLength={45}
                      value={form.pppoeLocalAddress} onChange={e => setField('pppoeLocalAddress', e.target.value)}
                      placeholder="10.0.0.1 (gateway)" aria-label="PPPoE local address" />
                  </label>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Parent Queue
                    <input style={modalStyles.input} type="text" maxLength={64}
                      value={form.pppoeParentQueue} onChange={e => setField('pppoeParentQueue', e.target.value)}
                      placeholder={form.seedPriorityQueues ? '01-GLOBAL-POP-LIMIT' : '(none)'} aria-label="PPPoE parent queue" />
                  </label>
                </div>
              </>
            )}

            <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.seedWalledGarden}
                onChange={e => setField('seedWalledGarden', e.target.checked)} aria-label="Seed walled garden" />
              Seed suspended-user walled garden
            </label>
            {form.seedWalledGarden && (
              <>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Suspended Address-List
                    <input style={modalStyles.input} type="text" maxLength={64}
                      value={form.suspendedListName} onChange={e => setField('suspendedListName', e.target.value)}
                      placeholder="fireisp-suspended" aria-label="Suspended address list" />
                  </label>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Portal Address (optional)
                    <input style={modalStyles.input} type="text" maxLength={255}
                      value={form.portalAddress} onChange={e => setField('portalAddress', e.target.value)}
                      placeholder="redirect target IP" aria-label="Portal address" />
                  </label>
                </div>
                {form.portalAddress && (
                  <>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <label style={{ ...modalStyles.label, flex: 1 }}>
                        Redirect Ports
                        <input style={modalStyles.input} type="text" maxLength={64}
                          value={form.redirectPorts} onChange={e => setField('redirectPorts', e.target.value)}
                          placeholder="80,443" aria-label="Redirect ports" />
                      </label>
                      <label style={{ ...modalStyles.label, flex: 1 }}>
                        Portal Listen Port
                        <input style={modalStyles.input} type="number" min={1} max={65535}
                          value={form.redirectToPort} onChange={e => setField('redirectToPort', e.target.value)}
                          placeholder="80" aria-label="Portal listen port" />
                      </label>
                    </div>
                    <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form.redirectEnabled}
                        onChange={e => setField('redirectEnabled', e.target.checked)} aria-label="Enable redirect on create" />
                      Lay the redirect down enabled (permanent). Uncheck to create it disabled for manual ordering.
                    </label>
                    {form.redirectEnabled && form.redirectPorts.includes('443') && (
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        Note: redirecting 443 to a plaintext portal triggers a browser TLS warning before the portal loads.
                      </p>
                    )}
                  </>
                )}
              </>
            )}

            <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.seedRealtimePriority}
                onChange={e => setField('seedRealtimePriority', e.target.checked)} aria-label="Seed realtime priority" />
              Prioritise real-time / VoIP & calling traffic
            </label>
            {form.seedRealtimePriority && (
              <>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    SIP/RTP Ports (UDP)
                    <input style={modalStyles.input} type="text" maxLength={128}
                      value={form.sipRtpPorts} onChange={e => setField('sipRtpPorts', e.target.value)}
                      placeholder="5060,5061,10000-20000" aria-label="SIP RTP ports" />
                  </label>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Realtime Queue Parent
                    <input style={modalStyles.input} type="text" maxLength={64}
                      value={form.realtimeParent} onChange={e => setField('realtimeParent', e.target.value)}
                      placeholder="global" aria-label="Realtime queue parent" />
                  </label>
                  <label style={{ ...modalStyles.label, flex: 1 }}>
                    Cap (Mbps, optional)
                    <input style={modalStyles.input} type="number" min={0}
                      value={form.realtimeMaxMbps} onChange={e => setField('realtimeMaxMbps', e.target.value)}
                      aria-label="Realtime cap Mbps" />
                  </label>
                </div>
                <label style={{ ...modalStyles.label }}>
                  OTT Provider Networks (fireisp-voip list)
                  <textarea style={{ ...modalStyles.input, minHeight: 44, fontFamily: 'monospace' }}
                    maxLength={2000}
                    value={form.voipNetworks} onChange={e => setField('voipNetworks', e.target.value)}
                    placeholder="CIDRs for WhatsApp/Meet/Zoom media, e.g. 157.240.0.0/16, 142.250.0.0/15"
                    aria-label="VoIP networks" />
                </label>
                <label style={{ ...modalStyles.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.trustClientDscp}
                    onChange={e => setField('trustClientDscp', e.target.checked)} aria-label="Trust client DSCP" />
                  Trust client-set DSCP EF (spoofable — recommend setting a cap above)
                </label>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  SIP/RTP is matched by port; encrypted OTT calls (WhatsApp, FaceTime, Meet) can only be matched by
                  the provider address-list above. fq-codel queue types are the foundation — enable them too.
                </p>
              </>
            )}

            {error && <p style={modalStyles.error}>{error}</p>}

            <div style={modalStyles.actions}>
              <button type="button" onClick={onClose} style={styles.btnSecondary} disabled={mutation.isPending}>
                Cancel
              </button>
              <button type="submit" style={styles.btnPrimary} disabled={mutation.isPending}>
                {mutation.isPending ? 'Seeding...' : 'Seed Device'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NasList component
// ---------------------------------------------------------------------------

export function NasList() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [showNew, setShowNew] = useState(false);

  const nasQ = useQuery({
    queryKey: ['nas', page, pageSize, statusFilter],
    queryFn: () => fetchNas(page, pageSize, statusFilter),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['nas'] });
  }

  function handleFilterChange(value: string) {
    setStatusFilter(value);
    setPage(1);
  }

  const devices = nasQ.data?.data ?? [];
  const meta = nasQ.data?.meta;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>NAS Devices</h1>
        {meta && <span style={styles.countBadge}>{meta.total} total</span>}
        <button style={{ ...styles.btnPrimary, marginLeft: 'auto' }} onClick={() => setShowNew(true)}>
          + New NAS
        </button>
      </div>

      <div style={styles.filterRow}>
        <label style={styles.filterLabel}>Status:</label>
        <select
          style={styles.filterSelect}
          value={statusFilter}
          onChange={e => handleFilterChange(e.target.value)}
        >
          {STATUS_FILTER_OPTIONS.map(s => (
            <option key={s} value={s}>{s ? capitalize(s) : 'All'}</option>
          ))}
        </select>
        {statusFilter && (
          <button type="button" style={styles.btnSecondary} onClick={() => handleFilterChange('')}>
            Clear filter
          </button>
        )}
      </div>

      <div style={styles.tableCard}>
        {nasQ.isLoading ? (
          <LoadingState />
        ) : nasQ.error ? (
          <p style={styles.msgError}>Failed to load NAS devices.</p>
        ) : devices.length === 0 ? (
          <p style={styles.msg}>No NAS devices found{statusFilter ? ` with status "${statusFilter}"` : ''}.</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['ID', 'Name', 'IP Address', 'Type', 'Ports', 'CoA Port', 'Health', 'Last Check', 'Status', t('nasList.maintenanceMode.column'), 'Actions'].map(
                      h => <th key={h} style={styles.th}>{h}</th>,
                    )}
                  </tr>
                </thead>
                <tbody>
                  {devices.map(n => (
                    <tr key={n.id} style={styles.tr}>
                      <td style={styles.td}>#{n.id}</td>
                      <td style={{ ...styles.td, fontWeight: 500 }}>{n.name}</td>
                      <td style={styles.td}>{n.ip_address}</td>
                      <td style={styles.td}>{n.type ?? '—'}</td>
                      <td style={styles.td}>{n.ports ?? '—'}</td>
                      <td style={styles.td}>{n.coa_port ?? '—'}</td>
                      <td style={styles.td}><HealthBadge status={n.health_status} /></td>
                      <td style={styles.td}>
                        {n.last_health_check_at
                          ? new Date(n.last_health_check_at).toLocaleString()
                          : '—'}
                      </td>
                      <td style={styles.td}><StatusBadge status={n.status} /></td>
                      <td style={styles.td}>
                        {Boolean(n.maintenance_mode) ? (
                          <span
                            title={t('nasList.maintenanceMode.badgeHint')}
                            style={{
                              background: '#e0e7ff',
                              color: '#3730a3',
                              padding: '2px 8px',
                              borderRadius: 12,
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {t('nasList.maintenanceMode.badge')}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                        <Link
                          to={`/nas/${n.id}`}
                          style={{ ...styles.actionBtn, textDecoration: 'none', display: 'inline-block' }}
                          title="Open this NAS to test, seed, configure WireGuard, refresh VoIP, edit or delete"
                        >
                          Manage →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={meta?.totalPages ?? 1}
              total={meta?.total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          </>
        )}
      </div>

      {showNew && (
        <NasModal
          nas={null}
          onClose={() => setShowNew(false)}
          onSaved={invalidate}
          // After creating a NAS, jump straight to its detail page — that's where
          // WireGuard, seeding and the rest of the per-device actions now live.
          onCreated={(nas) => navigate(`/nas/${nas.id}`)}
        />
      )}
    </div>
  );
}
